import { api } from "./api";

// VAPID public key fetched from server
let vapidKey = null;

export async function getVapidKey() {
  if (vapidKey) return vapidKey;
  try {
    const { data } = await api.get("/notifications/vapid-public-key");
    vapidKey = data.public_key || null;
    return vapidKey;
  } catch {
    return null;
  }
}

export function isPushSupported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function urlBase64ToUint8Array(base64String) {
  if (!base64String) return new Uint8Array();
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  try {
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
  } catch {
    return new Uint8Array();
  }
}

export async function getRegistration() {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error("Push notifications not supported");
  if (Notification.permission === "denied") throw new Error("Notifications are blocked. Enable in browser settings.");
  if (Notification.permission !== "granted") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") throw new Error("Permission denied");
  }
  const reg = await getRegistration();
  if (!reg) throw new Error("Service worker not ready");
  const key = await getVapidKey();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
  }
  const subJson = sub.toJSON();
  await api.post("/notifications/subscribe", {
    endpoint: subJson.endpoint,
    keys: subJson.keys || {},
    user_agent: navigator.userAgent,
  });
  return sub;
}

export async function unsubscribeFromPush() {
  const reg = await getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.post(`/notifications/unsubscribe?endpoint=${encodeURIComponent(sub.endpoint)}`);
    await sub.unsubscribe();
  }
}

export async function currentSubscriptionStatus() {
  if (!isPushSupported()) return { supported: false };
  const reg = await getRegistration();
  if (!reg) return { supported: true, registered: false, subscribed: false, permission: Notification.permission };
  const sub = await reg.pushManager.getSubscription();
  return { supported: true, registered: true, subscribed: !!sub, permission: Notification.permission };
}
